import { FC, useMemo, useState } from 'react'
import styles from 'components/features/videoCallPanel/VideoFrameWrapper/VideoFrameWrapper.styl'
import cn from 'classnames'
import FollowSvg from 'icons/follow.svg'
import CrossSvg from 'icons/cross.svg'
import PersonSvg from 'icons/person.svg'
import FullScreenSvg from 'icons/full-screen.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { VideoFrame } from 'components/features/videoCalls/VideoFrame/VideoFrame'
import Button from 'components/features/pfm/Button/Button'
import { StreamId } from 'components/features/videoCalls/types/agoraTypes'
import { createPeerSelector } from 'components/features/videoCalls/selectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { CallPeer } from 'components/features/videoCalls/types/state'
import { DEFAULT_VIDEO_WIDTH, DEFAULT_VIDEO_HEIGHT } from 'components/features/videoCalls/constants'

type VideoFrameWrapperProps = {
  id: StreamId | null
  isFullScreen: boolean
  isMainFrame?: boolean
  isPinned?: boolean
  onPin?: () => void
  onUnpin: () => void
  onFullScreen: () => void
  onExitFullScreen: () => void
}
export const VideoFrameWrapper: FC<VideoFrameWrapperProps> = (props) => {
  const peerSelector = useMemo(createPeerSelector, [])
  const peer = useStoreSelector((state) => peerSelector(state, props))

  if (!peer) {
    return null
  }

  return (
    <VideoFrameWrapperInner { ...props } peer={ peer } />
  )
}

type VideoFrameWrapperInnerProps =
  & VideoFrameWrapperProps
  & {
    peer: CallPeer
  }
const VideoFrameWrapperInner: FC<VideoFrameWrapperInnerProps> = (props) => {
  const { peer, isFullScreen, isMainFrame, isPinned, onPin, onUnpin, onFullScreen, onExitFullScreen } = props
  const { streamId, isVideoMuted, isAudioMuted, isReconnection, isScreenShare, isLocal } = peer

  const className = useMemo(() => cn(
    styles.video,
    {
      [styles.videoMuted]: isVideoMuted,
      [styles.fullScreen]: isFullScreen,
      [styles.mainFrame]: isMainFrame,
      [styles.mirrored]: isLocal,
    },
  ), [isVideoMuted, isFullScreen, isMainFrame, isLocal])

  const [size, setSize] = useState({ width: DEFAULT_VIDEO_WIDTH, height: DEFAULT_VIDEO_HEIGHT })
  const placeholderChild = useMemo(() => {
    if (isFullScreen && isMainFrame) {
      return (
        <svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%' className={ styles.placeholder } />
      )
    }

    return (
      <svg xmlns='http://www.w3.org/2000/svg' width={ size.width } height={ size.height } key={ `${ size.width }x${ size.height }` } className={ styles.placeholder } />
    )
  }, [isFullScreen, isMainFrame, size.height, size.width])

  return (
    <div className={ className }>
      { placeholderChild }
      <div className={ styles.videoWrapper }>
        <VideoFrame streamId={ streamId } onSizeChange={ setSize } />
      </div>
      <div className={ styles.person }>
        <div className={ styles.personWrapper }>
          <div className={ styles.personIconWrapper }>
            <div className={ styles.personIcon }>
              <SvgIcon src={ PersonSvg } />
            </div>
          </div>
        </div>
      </div>
      <div className={ styles.actions }>
        {
          isMainFrame && !isFullScreen && (
            <Button onClick={ onFullScreen } theme='darkTransparent'>
              <div className={ styles.iconWrapper }>
                <SvgIcon src={ FullScreenSvg } />
              </div>
            </Button>
          )
        }
        {
          isMainFrame && isFullScreen && (
            <Button onClick={ onExitFullScreen } theme='darkTransparent' >
              <div className={ styles.iconWrapper }>
                <SvgIcon src={ CrossSvg } />
              </div>
            </Button>
          )
        }
        {
          isMainFrame && isPinned && (
            <Button onClick={ onUnpin } theme='darkTransparent'>Unpin</Button>
          )
        }
        {
          !isMainFrame && !isPinned && !isScreenShare && !isLocal && (
            <div className={ styles.pinWrapper }>
              <Button onClick={ onPin } theme='darkTransparent'><SvgIcon src={ FollowSvg } /></Button>
            </div>
          )
        }
      </div>

      <div className={ cn(styles.statuses, isMainFrame && styles.isMainFrame) }>
        {
          isAudioMuted && (
            <div className={ styles.muted }>Muted</div>
          )
        }
        {
          isReconnection && (
            <div className={ styles.connecting }>Connecting...</div>
          )
        }
      </div>

    </div>
  )
}
